import React, { useState, useEffect } from 'react';
import { Download, Smartphone, Monitor, X, Check } from 'lucide-react';
import { toast } from 'sonner';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallAppButton() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop' | 'other'>('other');

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Detect platform
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isDesktop = !isIOS && !isAndroid;

    if (isIOS) {
      setPlatform('ios');
    } else if (isAndroid) {
      setPlatform('android');
    } else if (isDesktop) {
      setPlatform('desktop');
    }

    // Listen for beforeinstallprompt event (Chrome, Edge, Android)
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      const installEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(installEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      console.log('✅ Sentlo PWA installed successfully!');
      setIsInstalled(true);
      setShowInstructions(false);
      toast.success('Sentlo installed successfully! 🎉');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    // For iOS, show manual instructions
    if (platform === 'ios') {
      setShowInstructions(true);
      toast.info('Follow the steps to install', {
        description: 'iOS requires manual installation'
      });
      return;
    }

    // For Android/Desktop with deferred prompt
    if (deferredPrompt) {
      setIsInstalling(true);
      
      // Show immediate feedback
      toast.loading('Opening install prompt...', {
        id: 'install-prompt'
      });

      try {
        // Show the install prompt - this opens browser's native dialog
        await deferredPrompt.prompt();
        
        // Wait for user choice
        const choiceResult = await deferredPrompt.userChoice;
        
        if (choiceResult.outcome === 'accepted') {
          console.log('✅ User accepted the install prompt');
          toast.success('Installing Sentlo...', {
            id: 'install-prompt',
            description: 'App is being added to your home screen'
          });
          // App will be installed automatically now
        } else {
          console.log('❌ User dismissed the install prompt');
          toast.error('Installation cancelled', {
            id: 'install-prompt',
            description: 'You can try again anytime'
          });
        }
        
        setDeferredPrompt(null);
      } catch (error) {
        console.error('Error during installation:', error);
        toast.error('Could not open install prompt', {
          id: 'install-prompt',
          description: 'Try installing manually from browser menu'
        });
        // Fallback to instructions
        setShowInstructions(true);
      } finally {
        setIsInstalling(false);
      }
    } else {
      // No deferred prompt available - show manual instructions
      toast.info('Manual installation required', {
        description: 'Follow the browser instructions'
      });
      setShowInstructions(true);
    }
  };

  // Don't show button if already installed
  if (isInstalled) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
        <Check className="w-4 h-4 text-green-600" />
        <span className="text-sm font-semibold text-green-700">App Installed</span>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={handleInstallClick}
        disabled={isInstalling}
        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:shadow-lg hover:shadow-green-500/30 transition-all disabled:opacity-50 font-semibold text-sm"
      >
        {isInstalling ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Installing...
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            Install App
          </>
        )}
      </button>

      {/* iOS/Manual Instructions Modal */}
      {showInstructions && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[300] flex items-center justify-center p-4"
          onClick={() => setShowInstructions(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-100 p-3 rounded-xl">
                  {platform === 'ios' ? (
                    <Smartphone className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <Monitor className="w-6 h-6 text-emerald-600" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Install Sentlo App
                </h3>
              </div>
              <button
                onClick={() => setShowInstructions(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {platform === 'ios' ? (
              // iOS Instructions
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Add Sentlo to your home screen for quick access:
                </p>
                
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="bg-emerald-50 rounded-lg px-2 py-1 text-emerald-600 font-bold text-sm h-fit">1</div>
                    <p className="text-sm text-gray-700 flex-1">
                      Tap the <strong>Share</strong> button <span className="inline-block bg-blue-500 text-white px-2 py-0.5 rounded text-xs">⬆️</span> at the bottom of your screen
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <div className="bg-emerald-50 rounded-lg px-2 py-1 text-emerald-600 font-bold text-sm h-fit">2</div>
                    <p className="text-sm text-gray-700 flex-1">
                      Scroll down and tap <strong>"Add to Home Screen"</strong>
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <div className="bg-emerald-50 rounded-lg px-2 py-1 text-emerald-600 font-bold text-sm h-fit">3</div>
                    <p className="text-sm text-gray-700 flex-1">
                      Tap <strong>"Add"</strong> to complete installation
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 p-3 rounded-lg">
                  <Check className="w-4 h-4" />
                  <span>Works offline • Faster loading • App-like experience</span>
                </div>
              </div>
            ) : (
              // Android/Desktop Instructions
              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  To install Sentlo on your device:
                </p>
                
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="bg-emerald-50 rounded-lg px-2 py-1 text-emerald-600 font-bold text-sm h-fit">1</div>
                    <p className="text-sm text-gray-700 flex-1">
                      Look for the <strong>install icon</strong> in your browser's address bar (usually appears as <Download className="w-4 h-4 inline" /> or ⊕)
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <div className="bg-emerald-50 rounded-lg px-2 py-1 text-emerald-600 font-bold text-sm h-fit">2</div>
                    <p className="text-sm text-gray-700 flex-1">
                      Click it and select <strong>"Install Sentlo"</strong> or <strong>"Add to Home Screen"</strong>
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <div className="bg-emerald-50 rounded-lg px-2 py-1 text-emerald-600 font-bold text-sm h-fit">3</div>
                    <p className="text-sm text-gray-700 flex-1">
                      Or open your browser menu (⋮) and look for <strong>"Install app"</strong>
                    </p>
                  </div>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-xs text-emerald-800">
                    <strong>💡 Tip:</strong> Once installed, Sentlo works offline and loads faster than the web version!
                  </p>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowInstructions(false)}
              className="w-full mt-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition-colors"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
}